/**
 * Task — a reusable quality criterion that defines "what an agent or workflow
 * needs to get right." Composes one or more Graders (weighted Judge references)
 * and optionally references a GroundTruth entry as the canonical correct answer.
 *
 * Each grader's output normalizes to 0..1:
 *   boolean → true = 1, false = 0
 *   number  → value, clamped to [0, 1]
 *   enum    → looked up in TaskGrader.enumValueMap (author-supplied)
 *
 * Composite score = sum(weight × normalized) / sum(weight).
 *
 * Note: TaskGrader references Judge (not a separate "Grader" model) because
 * Judge already provides CODE + LLM_ONE_SHOT types with immutable versioning.
 * DEV-16269 renames Judge → Grader; DEV-16260 adds TOOL_POLICY as a new
 * JudgeType. Creating a parallel model would duplicate Judge's functionality.
 */
import { z } from 'zod';
import { EvaluationTargetKindSchema } from './eval.js';
import { EnumValueMapSchema } from './scorecard.js';
// ─── Scoring Policy ──────────────────────────────────────────────────────────
/**
 * Determines how grader scores combine into a per-Trial verdict.
 *
 * - all_pass: every grader must individually pass. Strictest mode.
 * - weighted_threshold: weighted composite score >= threshold. Allows partial
 *   credit — one grader failing doesn't necessarily fail the Trial.
 * - hybrid: all gate graders must pass AND the weighted composite of non-gate
 *   graders must meet the threshold. Best of both worlds — hard requirements
 *   (gates) plus nuanced scoring (weights).
 */
export const ScoringPolicySchema = z.object({
    mode: z.enum(['all_pass', 'weighted_threshold', 'hybrid']),
    /** Required for weighted_threshold and hybrid modes. Ignored in all_pass. */
    threshold: z.number().min(0).max(1).optional(),
});
// ─── KG Query Mode ───────────────────────────────────────────────────────────
export const KgQueryModeSchema = z.enum(['naive', 'local', 'global', 'hybrid', 'mix']);
export const kgQueryModes = KgQueryModeSchema.options;
// ─── Tracked Metric ──────────────────────────────────────────────────────────
/**
 * A measurement recorded on every Trial but not part of pass/fail scoring
 * unless a threshold is set. Examples: latency, turn count, token usage.
 *
 * When threshold IS set, the metric becomes a constraint — the Trial fails
 * if the metric exceeds the threshold (e.g., "must complete in < 30s").
 */
export const TrackedMetricSchema = z.object({
    type: z.enum(['latency', 'transcript']),
    /** When set, exceeding this value fails the metric constraint. */
    threshold: z.number().optional(),
    /** Type-specific config (e.g., which latency to measure, max_turns for transcript). */
    config: z.record(z.unknown()).default({}),
});
// ─── Built-in grader config schemas ─────────────────────────────────────────
export const GraderTypeSchema = z.enum([
    // Workflow-target built-ins (pre-unification)
    'state_check',
    'tool_calls',
    'transcript',
    // KG-target built-ins. `graderConfig` per type:
    //   - kg_answer_cited_recall: { threshold?: number }  (default 0.5)
    //   - kg_entity_recall / kg_entity_precision (Track B): { aliasSimilarity?: number }
    //   - kg_relation_f1 (Track B): { aliasSimilarity?: number }
    //   - kg_llm_rubric (Track B): { modelConfigId, rubric }  (judge-backed via TaskGrader.judgeId instead)
    'kg_answer_cited_recall',
    'kg_entity_recall',
    'kg_entity_precision',
    'kg_relation_f1',
    // MEMORY-target built-ins (DEV-17245). `graderConfig` per type:
    //   - memory_recall_at_k: { k?: number, threshold?: number }  (defaults 10 / 0.5)
    //   - memory_ndcg:        { k?: number, threshold?: number }  (defaults 10 / 0.5)
    'memory_recall_at_k',
    'memory_ndcg',
]);
// ─── KG task payload schemas ────────────────────────────────────────────────
/**
 * Shape of `Task.input` when `targetKind='KG'`. `queryMode` is optional
 * per-task; when null the executor falls back to the doc-set's default
 * (typically hybrid).
 */
export const KgTaskInputSchema = z.object({
    question: z.string().min(1).max(2000),
    queryMode: KgQueryModeSchema.optional(),
});
/**
 * Shape of `Task.expectedOutput` when `targetKind='KG'`. Every field is
 * optional at the schema level; the grader-side asserts the fields it
 * consumes (kg_answer_cited_recall needs `expectedCitations`, etc.).
 */
export const KgTaskExpectedOutputSchema = z.object({
    expectedAnswer: z.string().optional(),
    expectedCitations: z.array(z.string()).optional(),
    expectedEntities: z
        .array(z.object({
        name: z.string().min(1),
        type: z.string().min(1),
        aliases: z.array(z.string()).optional(),
    }))
        .optional(),
    expectedRelations: z
        .array(z.object({ src: z.string(), rel: z.string(), dst: z.string() }))
        .optional(),
});
// ─── MEMORY task payload schemas (DEV-17245) ────────────────────────────────
/**
 * The four retrieval modes of `searchMemories` — mirrored here (rather than
 * imported from `@cortex/database`) because types must stay dependency-free.
 */
export const MemoryProbeModeSchema = z.enum(['hybrid', 'recent', 'important', 'typed']);
/** Runtime list for `.includes()` guards — mirrors `kgQueryModes` so the
 *  executor can't drift from the schema. */
export const memoryProbeModes = MemoryProbeModeSchema.options;
/**
 * Shape of `Task.input` when `targetKind='MEMORY'` — one retrieval probe.
 * `question` is the query an agent would face mid-decision (drawn from real
 * run traces, not a conversational benchmark); `agentId` names whose
 * resolved memory set the probe searches. `limit` bounds the candidate list
 * the graders score over (recall@k reads its own `k` from grader config,
 * which must be <= this).
 */
export const MemoryTaskInputSchema = z.object({
    question: z.string().min(1).max(2000),
    agentId: z.string().min(1),
    mode: MemoryProbeModeSchema.optional(),
    /** Required by the executor when mode='typed' — a MemoryType name. */
    type: z.string().min(1).optional(),
    /** Hits requested from the store. Default 20 (2× the nDCG@10 horizon). */
    limit: z.number().int().min(1).max(50).optional(),
});
/**
 * One gold memory the probe should surface. Identified by row id (probes
 * authored against a known store, e.g. the seeded probe set) or by a
 * case-insensitive content substring (probes that must survive re-seeding).
 * At least one identifier is required.
 */
export const MemoryGoldSpecSchema = z
    .object({
    memoryId: z.string().min(1).optional(),
    contentMatch: z.string().min(1).optional(),
    /** Author note — why this memory is the right answer. Not scored. */
    note: z.string().optional(),
})
    .refine((g) => g.memoryId !== undefined || g.contentMatch !== undefined, {
    message: 'gold entry needs memoryId or contentMatch',
});
/**
 * Shape of `Task.expectedOutput` when `targetKind='MEMORY'`. `gold` may be
 * EMPTY — that is the write-miss probe: a fact the fleet needed that nobody
 * ever saved is expressed as `gold: [{ contentMatch: '...' }]` against a
 * store where it was deliberately never written, so an empty list is only
 * for "this query should return nothing relevant" floor probes.
 */
export const MemoryTaskExpectedOutputSchema = z.object({
    gold: z.array(MemoryGoldSpecSchema),
});
export const StateCheckSchema = z.object({
    path: z.string().min(1),
    operator: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains', 'matches']),
    value: z.unknown(),
});
export const StateCheckConfigSchema = z.array(StateCheckSchema);
export const ToolCallsConfigSchema = z.object({
    requiredTools: z.array(z.string()).optional(),
    forbiddenTools: z.array(z.string()).optional(),
    maxCalls: z.number().int().positive().optional(),
});
export const TranscriptConstraintsSchema = z.object({
    maxTurns: z.number().int().positive().optional(),
    maxTokens: z.number().int().positive().optional(),
    maxMessages: z.number().int().positive().optional(),
});
// ─── TaskGrader ─────────────────────────────────────────────────────────────
/**
 * A grader attached to a Task. Either judge-backed (judgeId set, graderType null)
 * or built-in (judgeId null, graderType + graderConfig set).
 */
export const TaskGraderSchema = z.object({
    id: z.string(),
    taskId: z.string(),
    /** FK to Judge — set for judge-backed graders, null for built-in. */
    judgeId: z.string().nullable(),
    weight: z.number().nonnegative(),
    enumValueMap: EnumValueMapSchema,
    /** When true, this grader is a must-pass gate (used in hybrid scoring mode). */
    gate: z.boolean().default(false),
    /** Built-in grader type. Null for judge-backed graders. */
    graderType: GraderTypeSchema.nullable().default(null),
    /** Type-specific config JSON. Null for judge-backed graders. */
    graderConfig: z.unknown().nullable().default(null),
    /** User-supplied name for built-in graders. Judge-backed use the Judge name. */
    name: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
});
// ─── Task ───────────────────────────────────────────────────────────────────
export const TaskSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /**
     * Target discriminator (). `WORKFLOW` (default)
     * preserves every pre-existing field's meaning. `KG` re-uses the same
     * Task row to represent a doc-set KG test case — `input` becomes
     * `{ question, queryMode? }`, `expectedOutput` is inline, and the
     * task-runner routes execution to `KgQueryExecutor` instead of the
     * workflow enqueue path.
     */
    targetKind: EvaluationTargetKindSchema.default('WORKFLOW'),
    /** KG-target only. Doc-set the task queries. Opaque metaphysics id. */
    documentSetId: z.string().nullable().default(null),
    /** Inline expected output. KG tasks always use this (shape:
     *  `KgTaskExpectedOutputSchema`). Workflow tasks may adopt later —
     *  today they still resolve via `groundTruthId`. */
    expectedOutput: z.record(z.unknown()).nullable().default(null),
    /** The scenario payload fed to the workflow. Each Task is a single test case — all N Trials use this same input. Null = legacy fallback to GT.input.
     *  For KG tasks the shape is `KgTaskInputSchema`. */
    input: z.record(z.unknown()).nullable().default(null),
    groundTruthId: z.string().nullable(),
    /** When set, scores this node's output instead of the workflow's final output. */
    targetNodeKey: z.string().nullable(),
    /** Legacy pass threshold — use `scoring` for new Tasks. */
    passThreshold: z.number().min(0).max(1).nullable(),
    /** Minimum Trials the runner must execute before the result is meaningful. */
    minTrials: z.number().int().positive().nullable().default(null),
    /** Scoring policy — how grader scores combine into a verdict. Null = backward compat (weighted_threshold with passThreshold). */
    scoring: ScoringPolicySchema.nullable().default(null),
    /** Metrics recorded per Trial but not part of pass/fail unless thresholded. */
    trackedMetrics: z.array(TrackedMetricSchema).nullable().default(null),
    /** Suite memberships — many-to-many. A Task can belong to multiple suites. Graduation from capability to regression = adding a suiteId. */
    suiteIds: z.array(z.string()).nullable().default(null),
    /** In template mode (input=null), filter GT entries by tags. Empty = no tag filter. */
    groundTruthTags: z.array(z.string()).default([]),
    /** In template mode (input=null), filter GT entries by ID. Empty = no ID filter. */
    groundTruthIds: z.array(z.string()).default([]),
    /** In template mode, pin GT expansion to a specific workflow. Null = use eval's workflow. */
    groundTruthWorkflowId: z.string().nullable().default(null),
    /** Keycloak subject ID of the person who created this Task. */
    createdBy: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.date().nullable(),
    /** Hydrated by the API when listing — empty when graders aren't included. */
    graders: z.array(TaskGraderSchema).default([]),
});
// ─── Inputs ─────────────────────────────────────────────────────────────────
/** Grader shape for create/update bodies (no id; taskId set from URL).
 *  Either provide `judgeId` (judge-backed) or `graderType` + `graderConfig` (built-in). */
export const TaskGraderInputSchema = z
    .object({
    /** Judge ID — required for judge-backed graders, omit for built-in. */
    judgeId: z.string().min(1).optional(),
    weight: z.number().nonnegative().default(1),
    enumValueMap: EnumValueMapSchema.optional(),
    /** Mark this grader as a must-pass gate for hybrid scoring. */
    gate: z.boolean().default(false),
    /** Built-in grader type. Omit for judge-backed graders. */
    graderType: GraderTypeSchema.optional(),
    /** Type-specific config. Required when graderType is set. */
    graderConfig: z.unknown().optional(),
    /** User-supplied name for built-in graders. */
    name: z.string().max(200).optional(),
})
    .refine((g) => {
    if (!g.graderType)
        return true;
    if (g.graderType === 'state_check')
        return StateCheckConfigSchema.safeParse(g.graderConfig).success;
    if (g.graderType === 'tool_calls')
        return ToolCallsConfigSchema.safeParse(g.graderConfig).success;
    if (g.graderType === 'transcript')
        return TranscriptConstraintsSchema.safeParse(g.graderConfig).success;
    return true;
}, {
    message: 'graderConfig does not match the schema for the specified graderType',
    path: ['graderConfig'],
});
export const CreateTaskInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    /** Task target. Defaults to WORKFLOW to preserve existing callers. */
    targetKind: EvaluationTargetKindSchema.optional(),
    /** Required when `targetKind='KG'`. Opaque metaphysics doc-set id. */
    documentSetId: z.string().min(1).optional(),
    /** Inline expected output (`KgTaskExpectedOutputSchema` for KG). */
    expectedOutput: z.record(z.unknown()).nullable().optional(),
    /** The scenario payload. The "question" for this test case. */
    input: z.record(z.unknown()).nullable().optional(),
    groundTruthId: z.string().min(1).nullable().optional(),
    /** Target a specific node in the workflow graph. Null = whole-workflow output. */
    targetNodeKey: z.string().min(1).max(200).nullable().optional(),
    /** Legacy — prefer `scoring.threshold` for new Tasks. */
    passThreshold: z.number().min(0).max(1).nullable().optional(),
    /** Minimum Trials per eval run. Must be >= 1 when set. */
    minTrials: z.number().int().positive().nullable().optional(),
    /** Scoring policy. Null = default to weighted_threshold with passThreshold. */
    scoring: ScoringPolicySchema.nullable().optional(),
    /** Tracked metrics (latency, transcript stats). */
    trackedMetrics: z.array(TrackedMetricSchema).nullable().optional(),
    /** Suite memberships. */
    suiteIds: z.array(z.string()).optional(),
    /** Template mode: filter GT entries by tags. */
    groundTruthTags: z.array(z.string()).optional(),
    /** Template mode: filter GT entries by ID. */
    groundTruthIds: z.array(z.string()).optional(),
    /** Template mode: pin to a specific workflow's GT entries. Null = eval's workflow. */
    groundTruthWorkflowId: z.string().nullable().optional(),
    graders: z.array(TaskGraderInputSchema).default([]),
});
/**
 * Partial update. `graders` (when supplied) replaces the full set — easier
 * to reason about than per-grader add/remove diffs and the table is small.
 */
export const UpdateTaskInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    documentSetId: z.string().min(1).nullable().optional(),
    expectedOutput: z.record(z.unknown()).nullable().optional(),
    input: z.record(z.unknown()).nullable().optional(),
    groundTruthId: z.string().min(1).nullable().optional(),
    targetNodeKey: z.string().min(1).max(200).nullable().optional(),
    passThreshold: z.number().min(0).max(1).nullable().optional(),
    minTrials: z.number().int().positive().nullable().optional(),
    scoring: ScoringPolicySchema.nullable().optional(),
    trackedMetrics: z.array(TrackedMetricSchema).nullable().optional(),
    suiteIds: z.array(z.string()).optional(),
    groundTruthTags: z.array(z.string()).optional(),
    groundTruthIds: z.array(z.string()).optional(),
    groundTruthWorkflowId: z.string().nullable().optional(),
    graders: z.array(TaskGraderInputSchema).optional(),
});
// ─── EvaluationTask (Evaluation ↔ Task join) ────────────────────────────────
/** Row from the evaluation_task join table. */
export const EvaluationTaskSchema = z.object({
    id: z.string(),
    evaluationId: z.string(),
    taskId: z.string(),
    enabled: z.boolean(),
    createdAt: z.string().datetime(),
});
/** Body for `PUT /v1/evaluations/:id/tasks/:taskId`. */
export const UpsertEvaluationTaskInputSchema = z.object({
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=task.js.map